import {
  Autocomplete,
  Button,
  Chip,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import {
  MdAdd,
  MdCheck,
  MdClose,
  MdDelete,
  MdEdit,
  MdRefresh,
} from '@teamworksdev/icons';
import { useCallback, useMemo, useState } from 'react';
import { trpc } from '../../../../trpc/react';
import { Page } from '../../../components/layout/page';
import { PageHeader } from '../../../components/layout/page-header';
import { ConfirmationModal } from '../../../components/modals/confirmation-modal';
import { TabPanel, TabTrigger, Tabs } from '../../../components/tab/tab';
import { DataTableWithContext } from '../../../components/table/data-table';
import {
  createColumnHelper,
  type CheckboxFilter,
} from '../../../components/table/data-table-context';
import { cn } from '../../../components/utils';
import { useToast } from '../../../hooks/useToast';
import { CategoryDrawer } from './CategoryDrawer';
import { EditSystemMetricDrawer } from './EditSystemMetricDrawer';
import { MetricFormDrawer } from './MetricFormDrawer';
import type { AdminCategory, AdminMetric } from './types';
import { countMetricsInCategory } from './utils';

type OrgOption = { id: string; name: string };

type MetricRow = AdminMetric & { categoryName: string };

const columnHelper = createColumnHelper<MetricRow>();
const categoryColumnHelper = createColumnHelper<AdminCategory>();

export const MetricsPage = () => {
  const { toast } = useToast();

  const [selectedOrg, setSelectedOrg] = useState<OrgOption | null>(null);
  const [activeTab, setActiveTab] = useState<'metrics' | 'categories'>(
    'metrics',
  );
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState<AdminMetric | undefined>();
  const [editingSystemMetric, setEditingSystemMetric] = useState<
    AdminMetric | undefined
  >();
  const [showDeleted, setShowDeleted] = useState(false);
  const [metricSearch, setMetricSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [metricToDelete, setMetricToDelete] = useState<
    AdminMetric | undefined
  >();
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<
    AdminCategory | undefined
  >();
  const [categoryToDelete, setCategoryToDelete] = useState<
    AdminCategory | undefined
  >();

  const showDeletedFilter: CheckboxFilter = {
    type: 'checkbox',
    id: 'showDeleted',
    label: 'Show deleted',
    onChange: setShowDeleted,
  };

  const orgsQuery = trpc.admin.orgs.useQuery(undefined, {
    staleTime: 10_000,
  });

  const metricsQuery = trpc.admin.metrics.getMetricsByOrg.useQuery(
    { orgId: selectedOrg?.id ?? '', includeDeleted: true },
    { enabled: !!selectedOrg },
  );

  const categoriesQuery = trpc.admin.metrics.getCategoriesByOrg.useQuery(
    { orgId: selectedOrg?.id ?? '' },
    { enabled: !!selectedOrg },
  );

  const deleteCategoryMutation = trpc.admin.metrics.deleteCategory.useMutation({
    onSuccess: () => {
      categoriesQuery.refetch();
      metricsQuery.refetch();
      toast({ title: 'Category deleted', severity: 'success' });
    },
    onError: () => {
      toast({ title: 'Error deleting category', severity: 'error' });
    },
  });

  const openCreateCategory = useCallback(() => {
    setEditingCategory(undefined);
    setIsCategoryDrawerOpen(true);
  }, []);

  const openEditCategory = useCallback((category: AdminCategory) => {
    setEditingCategory(category);
    setIsCategoryDrawerOpen(true);
  }, []);

  const deleteMutation = trpc.admin.metrics.deleteMetric.useMutation({
    onSuccess: () => {
      metricsQuery.refetch();
      toast({ title: 'Metric deleted', severity: 'success' });
    },
    onError: () => {
      toast({ title: 'Error deleting metric', severity: 'error' });
    },
  });

  const restoreMutation = trpc.admin.metrics.restoreMetric.useMutation({
    onSuccess: () => {
      metricsQuery.refetch();
      toast({ title: 'Metric restored', severity: 'success' });
    },
    onError: () => {
      toast({ title: 'Error restoring metric', severity: 'error' });
    },
  });

  const orgOptions: OrgOption[] = useMemo(
    () =>
      (orgsQuery.data ?? []).map((org) => ({
        id: org.id,
        name: org.name,
      })),
    [orgsQuery.data],
  );

  const categories = useMemo(
    () => categoriesQuery.data ?? [],
    [categoriesQuery.data],
  );

  const categoryById = useMemo(
    () =>
      new Map(categories.map((category) => [category.id, category] as const)),
    [categories],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: 'Name',

        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'line-through opacity-50')}>
              {info.getValue()}
            </span>
          );
        },
      }),
      columnHelper.accessor('type', {
        header: 'Type',

        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <Chip
              label={info.getValue()}
              size="small"
              variant="outlined"
              className={cn(isDeleted && 'opacity-50')}
            />
          );
        },
      }),
      columnHelper.accessor('categoryName', {
        id: 'category',
        header: 'Category',
        enableSorting: true,
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.getValue() || '—'}
            </span>
          );
        },
      }),
      columnHelper.accessor('sortOrder', {
        header: 'Sort Order',
        enableSorting: true,
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.getValue()}
            </span>
          );
        },
      }),
      columnHelper.accessor('isCollectable', {
        header: 'Collectable',

        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.getValue() ? (
                <MdCheck size={18} className="text-green-600" />
              ) : (
                <MdClose size={18} className="text-gray-400" />
              )}
            </span>
          );
        },
      }),
      columnHelper.accessor('isDefault', {
        header: 'Default',

        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.getValue() ? (
                <MdCheck size={18} className="text-green-600" />
              ) : (
                <MdClose size={18} className="text-gray-400" />
              )}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'numericType',
        header: 'Numeric Type',
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.row.original.config.numericType}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'allowRange',
        header: 'Range',
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.row.original.config.allowRange ? (
                <MdCheck size={18} className="text-green-600" />
              ) : (
                <MdClose size={18} className="text-gray-400" />
              )}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'isPercent',
        header: 'Percentage',
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.row.original.config.isPercent ? (
                <MdCheck size={18} className="text-green-600" />
              ) : (
                <MdClose size={18} className="text-gray-400" />
              )}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'units',
        header: 'Units',
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          const units = info.row.original.config.units;
          return (
            <span className={cn('text-xs', isDeleted && 'opacity-50')}>
              {units.length > 0 ? units.join(', ') : '-'}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'keywords',
        header: 'Keywords',
        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          const keywords = info.row.original.config.keywords;
          return (
            <span className={cn('text-xs', isDeleted && 'opacity-50')}>
              {keywords.length > 0 ? keywords.join(', ') : '-'}
            </span>
          );
        },
      }),
      columnHelper.accessor('recordType', {
        header: 'Record Type',

        cell: (info) => {
          const isDeleted = !!info.row.original.deletedAt;
          return (
            <span className={cn(isDeleted && 'opacity-50')}>
              {info.getValue() ?? '-'}
            </span>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const metric = info.row.original;
          const isDeleted = !!metric.deletedAt;

          if (isDeleted) {
            return (
              <div className="flex gap-1">
                <Tooltip title="Restore">
                  <IconButton
                    size="small"
                    aria-label={`Restore metric ${metric.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (selectedOrg) {
                        restoreMutation.mutate({
                          orgId: selectedOrg.id,
                          id: metric.id,
                        });
                      }
                    }}
                  >
                    <MdRefresh size={18} />
                  </IconButton>
                </Tooltip>
              </div>
            );
          }

          const isSystemMetric =
            metric.type === 'reps' || metric.type === 'intensity';

          return (
            <div className="flex gap-1">
              {isSystemMetric ? (
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    aria-label={`Edit ${metric.name}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingSystemMetric(metric);
                    }}
                  >
                    <MdEdit size={18} />
                  </IconButton>
                </Tooltip>
              ) : (
                <>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      aria-label={`Edit metric ${metric.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingMetric(metric);
                        setIsDrawerOpen(true);
                      }}
                    >
                      <MdEdit size={18} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      aria-label={`Delete metric ${metric.name}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMetricToDelete(metric);
                      }}
                    >
                      <MdDelete size={18} />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </div>
          );
        },
      }),
    ],
    [selectedOrg, restoreMutation],
  );

  const metrics = useMemo(() => metricsQuery.data ?? [], [metricsQuery.data]);
  const nextSortOrder =
    metrics.length > 0 ? Math.max(...metrics.map((m) => m.sortOrder)) + 1 : 0;

  // The category name is joined onto each row rather than resolved inside the
  // column accessor: TanStack caches accessor values per row and only recomputes
  // when `data` changes, so a category rename (which doesn't touch metric rows)
  // would leave the table showing the old name.
  const filteredMetrics = useMemo(() => {
    let result = metrics
      .filter((m) => m.type !== 'sets')
      .map((m) => ({
        ...m,
        categoryName:
          (m.categoryId ? categoryById.get(m.categoryId)?.name : undefined) ??
          '',
      }));
    if (!showDeleted) {
      result = result.filter((m) => !m.deletedAt);
    }
    const query = metricSearch.trim().toLowerCase();
    if (query) {
      result = result.filter(
        (m) =>
          m.name.toLowerCase().includes(query) ||
          m.categoryName.toLowerCase().includes(query) ||
          m.config.keywords.some((k) => k.toLowerCase().includes(query)),
      );
    }
    return result;
  }, [metrics, showDeleted, categoryById, metricSearch]);

  const filteredCategories = useMemo(() => {
    const query = categorySearch.trim().toLowerCase();
    if (!query) {
      return categories;
    }
    return categories.filter((c) => c.name.toLowerCase().includes(query));
  }, [categories, categorySearch]);

  const categoryToDeleteMetricCount = categoryToDelete
    ? countMetricsInCategory(metrics, categoryToDelete.id)
    : 0;

  const categoryColumns = useMemo(
    () => [
      categoryColumnHelper.accessor('name', {
        header: 'Name',
        cell: (info) => info.getValue(),
      }),
      categoryColumnHelper.display({
        id: 'metricCount',
        header: '# Metrics',
        cell: (info) => countMetricsInCategory(metrics, info.row.original.id),
      }),
      categoryColumnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: (info) => {
          const category = info.row.original;

          return (
            <div className="flex gap-1">
              <Tooltip title="Edit">
                <IconButton
                  size="small"
                  aria-label={`Edit category ${category.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditCategory(category);
                  }}
                >
                  <MdEdit size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                <IconButton
                  size="small"
                  aria-label={`Delete category ${category.name}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCategoryToDelete(category);
                  }}
                >
                  <MdDelete size={18} />
                </IconButton>
              </Tooltip>
            </div>
          );
        },
      }),
    ],
    [metrics, openEditCategory],
  );

  return (
    <Page>
      <PageHeader
        title="Metrics"
        rightSlot={
          <div className="w-80">
            <Autocomplete
              options={orgOptions}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              value={selectedOrg}
              onChange={(_, value) => setSelectedOrg(value)}
              loading={orgsQuery.isLoading}
              renderInput={(params) => (
                <TextField {...params} placeholder="Select Organization" />
              )}
            />
          </div>
        }
      />
      <div className="flex flex-col p-6 flex-1 min-h-0 gap-4">
        <Tabs
          value={activeTab}
          onChange={(_, value) => {
            setActiveTab(value);
            setMetricSearch('');
            setCategorySearch('');
          }}
          aria-label="Metrics sections"
          className="w-[400px]"
        >
          <TabTrigger label="Metrics" value="metrics" />
          <TabTrigger label="Categories" value="categories" />
        </Tabs>
        <div className="flex flex-1 overflow-hidden">
          <TabPanel
            value={activeTab}
            tab="metrics"
            className="flex flex-1 min-h-0 min-w-0"
          >
            <DataTableWithContext<MetricRow>
              title={
                selectedOrg ? `${filteredMetrics.length} Metrics` : 'Metrics'
              }
              columns={columns}
              data={filteredMetrics}
              enablePagination={false}
              enableSorting={true}
              initialSorting={[{ id: 'sortOrder', desc: false }]}
              enableSearch={true}
              searchPlaceholder="Search metrics..."
              onSearchChange={setMetricSearch}
              enableFilters={!!selectedOrg}
              filters={[showDeletedFilter]}
              getRowId={(row) => row.id}
              renderHeaderActions={() =>
                selectedOrg ? (
                  <Button
                    onClick={() => {
                      setEditingMetric(undefined);
                      setIsDrawerOpen(true);
                    }}
                  >
                    Create Metric
                    <MdAdd color="white" className="ml-1" />
                  </Button>
                ) : null
              }
            />
          </TabPanel>
          <TabPanel
            value={activeTab}
            tab="categories"
            className="flex flex-1 min-h-0 min-w-0"
          >
            <DataTableWithContext<AdminCategory>
              title={
                selectedOrg
                  ? `${filteredCategories.length} Categories`
                  : 'Categories'
              }
              columns={categoryColumns}
              data={filteredCategories}
              enablePagination={false}
              enableSorting={true}
              enableSearch={true}
              searchPlaceholder="Search categories..."
              onSearchChange={setCategorySearch}
              getRowId={(row) => row.id}
              renderHeaderActions={() =>
                selectedOrg ? (
                  <Button onClick={openCreateCategory}>
                    Create Category
                    <MdAdd color="white" className="ml-1" />
                  </Button>
                ) : null
              }
            />
          </TabPanel>
        </div>
      </div>

      {isDrawerOpen && selectedOrg && (
        <MetricFormDrawer
          open={isDrawerOpen}
          onClose={() => {
            setIsDrawerOpen(false);
            setEditingMetric(undefined);
          }}
          orgId={selectedOrg.id}
          metric={editingMetric}
          defaultSortOrder={nextSortOrder}
          onSuccess={() => {
            metricsQuery.refetch();
            setIsDrawerOpen(false);
            setEditingMetric(undefined);
          }}
        />
      )}

      {editingSystemMetric && selectedOrg && (
        <EditSystemMetricDrawer
          open
          onClose={() => setEditingSystemMetric(undefined)}
          orgId={selectedOrg.id}
          metric={editingSystemMetric}
          onSuccess={() => {
            metricsQuery.refetch();
            setEditingSystemMetric(undefined);
          }}
        />
      )}

      <ConfirmationModal
        id="delete-metric-confirm"
        open={!!metricToDelete}
        title={`Delete "${metricToDelete?.name}"?`}
        message="This metric will be soft-deleted. You can restore it later."
        onConfirm={() => {
          if (metricToDelete && selectedOrg) {
            deleteMutation.mutate({
              orgId: selectedOrg.id,
              id: metricToDelete.id,
            });
          }
          setMetricToDelete(undefined);
        }}
        onCancel={() => setMetricToDelete(undefined)}
      />

      {/* Mounted only while open, like MetricFormDrawer: the form's
          defaultValues are read once at mount, so a drawer left mounted would
          keep the previous category's name — and would not prefill on edit. */}
      {isCategoryDrawerOpen && selectedOrg && (
        <CategoryDrawer
          open
          onClose={() => {
            setIsCategoryDrawerOpen(false);
            setEditingCategory(undefined);
          }}
          orgId={selectedOrg.id}
          category={editingCategory}
          categories={categories}
          onSuccess={() => {
            categoriesQuery.refetch();
            setIsCategoryDrawerOpen(false);
            setEditingCategory(undefined);
          }}
        />
      )}

      <ConfirmationModal
        id="delete-category-confirm"
        open={!!categoryToDelete}
        title={`Delete "${categoryToDelete?.name}"?`}
        message={
          categoryToDeleteMetricCount === 0
            ? undefined
            : categoryToDeleteMetricCount === 1
              ? '1 metric will become uncategorized.'
              : `${categoryToDeleteMetricCount} metrics will become uncategorized.`
        }
        onConfirm={() => {
          if (categoryToDelete && selectedOrg) {
            deleteCategoryMutation.mutate({
              orgId: selectedOrg.id,
              id: categoryToDelete.id,
            });
          }
          setCategoryToDelete(undefined);
        }}
        onCancel={() => setCategoryToDelete(undefined)}
      />
    </Page>
  );
};
